<template>
  <div>
    <div class="office-name" data-testid="office-name">
      {{ officeName }}
    </div>
    <div class="position-relative">
      <BImgLazy :src="optimizedOrderImage" alt="" class="office-image" />
      <div class="foreground-area">
        <Tag :label="officeServiceName" class="office-service-tag" />
      </div>
    </div>
    <div class="p-3 border-bottom border-gray-03">
      <div>
        <div class="text-weight-500">
          {{ address }}
        </div>
        <div class="mt-1">
          <EvaluationWithCount
            v-if="evaluationTotalAverage"
            :value="evaluationTotalAverage"
            :count="evaluationCount"
          />
        </div>
      </div>
      <div class="office-actions mt-2">
        <FavoriteButton
          :isFavorite="isFavorite"
          @click="$emit('favorite-change', $event)"
        />
        <OfficeMuteButton
          :isMuted="isMutedOffice"
          @click="$emit('change-muted-office', $event)"
        />
      </div>
    </div>
    <div class="p-3">
      <div>
        <div class="text-weight-500 text-mild">責任者</div>
        <GreetingMessage
          class="greeting-message"
          :firstName="getOfficeProp('managerFirstName')"
          :lastName="getOfficeProp('managerLastName')"
          :personImage="getOfficeProp('managerAvatar')"
          :message="getOfficeProp('greetingMessage')"
        />
      </div>
      <div class="operation-tags" v-if="limitedOperations.length">
        <Tag
          v-for="operation in limitedOperations"
          :key="operation.id"
          :label="operation.name"
          class="bg-light-yellow border-yellow text-primary-dark"
          data-testid="operation-tag"
        />
        <span>など</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { Office } from "@/types/office/Office";
import EvaluationWithCount from "@/components/molecules/other/EvaluationWithCount.vue";
import FavoriteButton from "../common/FavoriteButton.vue";
import { MostFrequentOrderTemplate } from "@/types/office/MostFrequentOrderTemplate";
import { getOptimizedImageUrl } from "@/utils/image";
import GreetingMessage from "@/components/molecules/other/GreetingMessage.vue";
import Tag from "@/components/atoms/other/Tag.vue";
import { BImgLazy } from "bootstrap-vue";
import OfficeMuteButton from "@/components/organisms/common/OfficeMuteButton.vue";

const MAX_OPERATION_LENGTH = 10;

@Component({
  components: {
    EvaluationWithCount,
    FavoriteButton,
    OfficeMuteButton,
    GreetingMessage,
    Tag,
    BImgLazy
  }
})
export default class OfficeInformation extends Vue {
  @Prop({ type: Object, default: null })
  office!: Office | null;
  @Prop({ type: String, default: "" })
  officeServiceName!: string;
  @Prop({ type: Number, default: null })
  evaluationTotalAverage!: number | null;
  @Prop({ type: Number, default: 0 })
  evaluationCount!: number;
  @Prop({ type: Object, default: null })
  mostFrequentOrderTemplate!: MostFrequentOrderTemplate | null;
  @Prop({ type: Boolean, default: false })
  isFavorite!: boolean;
  @Prop({ type: Boolean, default: false })
  isMutedOffice!: boolean;

  get address() {
    return this.office?.address || "";
  }

  get officeName() {
    return this.office?.name || "";
  }

  get limitedOperations() {
    return (
      this.mostFrequentOrderTemplate?.operations.slice(
        0,
        MAX_OPERATION_LENGTH
      ) || []
    );
  }

  get optimizedOrderImage() {
    if (!this.mostFrequentOrderTemplate?.image1) return "";
    return getOptimizedImageUrl(
      this.mostFrequentOrderTemplate.image1,
      768,
      null
    );
  }

  getOfficeProp(fieldName: string) {
    if (!this.office) return "";
    return this.office[fieldName as keyof Office];
  }
}
</script>

<style lang="scss" scoped>
.office-name {
  padding: 8px 16px 12px;
  font-weight: 500;
  word-break: break-all;
}

.office-image {
  width: 100%;
  height: 208px;
  object-fit: cover;
}

.foreground-area {
  position: absolute;
  top: 8px;
  right: 8px;
  left: 8px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.greeting-message {
  margin-top: 12px;
}

.operation-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: center;
  align-items: center;
  margin-top: 12px;
}

.office-service-tag {
  padding: 4px 6px;
  color: $blue;
  background-color: $white;
  border-color: $blue;
}

.office-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}
</style>
